import clsx from 'clsx';
import { Fragment, type JSX } from 'react';
import { PathUtils } from 'ts/commons/PathUtils';
import { UniformPath } from 'ts/commons/UniformPath';
import { Breadcrumb, BreadcrumbDivider, BreadcrumbSection } from 'ts/components/Breadcrumb';
import type { SemanticSIZES } from 'ts/components/Generic';
import { EType } from 'typedefs/EType';
import styles from './VirtualFileAwareUniformPathBreadcrumb.module.less';

/** Props for ArchitectureAwareUniformPathBreadcrumb. */
type VirtualFileAwareUniformPathBreadcrumbProps = {
	className?: string;
	uniformPath: UniformPath | string[];
	project?: string;
	getPropsForCrumb: (index: number, currentPath: string[], isLastCrumb: boolean) => BreadCrumbSectionProps;
	elementAfterPath?: JSX.Element;
	showPrefixEntry?: boolean;
	divider?: JSX.Element;
	size?: SemanticSIZES;
};

/** Props for a single breadcrumb section. */
type BreadCrumbSectionProps = {
	active?: boolean;
	as?: unknown;
	className?: string;
	content?: string;
	link?: boolean;
	onClick?: () => void;
	title?: string;
	to?: string | undefined;
};

/**
 * A breadcrumb component that renders a path in a uniform way. The path is split into an upper and lower path, where
 * the upper path is the container path and the lower path is component path.
 */
export function VirtualFileAwareUniformPathBreadcrumb({
	className,
	uniformPath,
	project,
	getPropsForCrumb,
	elementAfterPath,
	showPrefixEntry,
	divider,
	size
}: VirtualFileAwareUniformPathBreadcrumbProps): JSX.Element | null {
	let crumbs;
	if (Array.isArray(uniformPath)) {
		crumbs = uniformPath;
	} else {
		crumbs = uniformPath.getSegments();
	}

	// Return if there are only artificial segments in the path so that the breadcrumb is not rendered because there is
	// nothing to display.
	if (crumbs.every(crumb => PathUtils.isArtificialArchitectureSegment(crumb))) {
		return null;
	}

	if (project) {
		crumbs.unshift(project);
	}

	const { containerCrumbs: filePathCrumbs, componentCrumbs: componentPathCrumbs } = PathUtils.splitVirtualPath(
		crumbs,
		false
	);
	return (
		<Breadcrumb size={size} className={clsx(className, styles.selectBreadcrumb)}>
			{renderCrumbs(filePathCrumbs, crumbs.length, getPropsForCrumb, showPrefixEntry, divider)}
			{componentPathCrumbs.length > 0 ? (
				<div className="lower-breadcrumbs">
					<span className="opacity-50">Code in {detectContainerType(crumbs)}: </span>
					{renderCrumbs(componentPathCrumbs, crumbs.length, getPropsForCrumb, false, divider, filePathCrumbs)}
					{elementAfterPath}
				</div>
			) : (
				elementAfterPath
			)}
		</Breadcrumb>
	);
}

/**
 * Detects whether the "container" of the provided virtual path is a "component" (for architecture paths) or a "code
 * scope" (for code-scope paths)
 */
export function detectContainerType(path: string | string[]): 'component' | 'code scope' {
	const uniformPathType = PathUtils.resolveUniformPathType(path);
	if (uniformPathType === EType.CODE_SCOPES) {
		return 'code scope';
	}
	return 'component';
}

function renderCrumbs(
	crumbs: string[],
	totalCrumbs: number,
	getPropsForCrumb: (index: number, currentPath: string[], isLastCrumb: boolean) => BreadCrumbSectionProps,
	showPrefixEntry = false,
	divider: JSX.Element = <BreadcrumbDivider />,
	pathPrefix: string[] = []
): Array<JSX.Element | null> {
	const indexOffset = pathPrefix.length;
	let isFirstVisibleItem = true;
	const showDivider = () => {
		if (isFirstVisibleItem) {
			isFirstVisibleItem = false;
			return false;
		}
		return true;
	};

	return crumbs.map((crumb, index) => {
		const currentOffset = index + indexOffset;
		pathPrefix.push(crumb);

		const isCrumbPathPrefix = PathUtils.isPathPrefix(crumb);
		// don't render if the crumb is an artificial segment, or if it's a path prefix, but the breadcrumbs should not be navigable to top level
		if (PathUtils.isArtificialArchitectureSegment(crumb) || (isCrumbPathPrefix && !showPrefixEntry)) {
			return null;
		}
		const props = getPropsForCrumb(currentOffset, pathPrefix, currentOffset === totalCrumbs - 1);

		const crumbText = isCrumbPathPrefix ? `All ${pathPrefixToKind(crumb)}` : crumb;
		return (
			<Fragment key={currentOffset}>
				{showDivider() ? divider : null}
				{/* @ts-ignore */}
				<BreadcrumbSection content={crumbText} title={crumbText} {...props} />
			</Fragment>
		);
	});
}

function pathPrefixToKind(prefix: string) {
	const uniformPath = new UniformPath(prefix);
	switch (uniformPath.type) {
		case EType.CODE:
			return 'Files';
		case EType.NON_CODE:
			return 'Elements';
		case EType.ARCHITECTURE:
			return 'Architectures';
		case EType.TEST_IMPLEMENTATION:
		case EType.TEST_EXECUTION:
			return 'Tests';
		case EType.TEST_QUERY:
		case EType.ISSUE_QUERY:
			return 'Queries';
		case EType.ISSUE_ITEM:
			return 'Issues';
		case EType.SPEC_ITEM:
		case EType.SPEC_ITEM_QUERY:
			return 'Spec Items';
		case EType.EXECUTION_UNIT:
			return 'Execution Units';
		case EType.CODE_SCOPES:
			return 'Code Scopes';
		default:
			throw new Error('The given uniform path type ' + uniformPath.type + ' is not supported by the breadcrumbs');
	}
}
